import React, {memo, useCallback, useEffect, useRef, useState} from 'react';

import {FmlxToast} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';

import {hideToast} from '@/actions/toastStateAction';
import useDebounce from '@/hooks/useDebounce';
import useDebounceValue from '@/hooks/useDebounceValue';
import useDetectOutsideClick from '@/hooks/useDetectOutsideClick';
import {getMainToast} from '@/selectors/toastSelector';

const SWITCH_CONTENT_TIMEOUT = 200;

function MainToast({autoHide}) {
  const dispatch = useDispatch();
  const {show, variant, title, content, timeout, placement, id} = useSelector(getMainToast);
  const [open, setOpen] = useState(show);
  const toastRef = useRef();

  // #region handle switch between content
  const {
    variant: _variant,
    title: _title,
    content: _content,
  } = useDebounceValue({variant, title, content}, SWITCH_CONTENT_TIMEOUT);

  useEffect(() => {
    setOpen(show);
    if (show) {
      setOpen(false);
      setTimeout(() => {
        setOpen(true);
      }, SWITCH_CONTENT_TIMEOUT + 100);
    }
  }, [id, show]);
  // #endregion

  // #region handle close toast
  const handleClose = useCallback(() => {
    dispatch(hideToast());
  }, [dispatch]);

  useDetectOutsideClick(toastRef, () => handleClose(), [handleClose], [show]);

  useDebounce(
    () => {
      if (autoHide && show) handleClose();
    },
    timeout,
    [id, show, timeout, autoHide, handleClose],
  );
  // #endregion

  return (
    <FmlxToast
      ref={toastRef}
      content={_content}
      open={open}
      placement={placement ?? FmlxToast.Placement.TOP_CENTER}
      title={_title}
      variant={_variant}
      onClick={handleClose}
    />
  );
}

MainToast.propTypes = {
  autoHide: PropTypes.bool,
};

MainToast.defaultProps = {
  autoHide: true,
};

export default memo(MainToast);
